import React, { useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { BaseToolResult } from "./BaseToolResult";
import { DocumentLink } from "../../../../components/DocumentLink";
import { PropertyInsightQueryResult } from "@stratify/shared";
import { PropertyInsightsToolArgs } from "./toolLoadingRegistry";

interface PropertyInsightsToolResultProps {
  result: PropertyInsightQueryResult;
  args?: PropertyInsightsToolArgs;
}

export const PropertyInsightsToolResult: React.FC<
  PropertyInsightsToolResultProps
> = ({ result, args }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { summary, relevantDocuments } = result;
  const query = args?.query || "Property insights query";

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);


  const getApprovalChip = (approved?: boolean) => {
    if (approved === true) {
      return <Chip label="Approved" color="success" size="small" />;
    } else if (approved === false) {
      return <Chip label="Not Approved" color="error" size="small" />;
    }
    return null;
  };

  return (
    <>
      <BaseToolResult icon="🏠">
        <Box
          onClick={handleOpenModal}
          sx={{
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "action.hover",
            },
            borderRadius: 1,
            p: 1,
            transition: "background-color 0.2s",
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            Found {summary.totalRelevantInsights} relevant insights from{" "}
            {summary.documentsWithRelevantInsights} documents
          </Typography>
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            Click to view details
          </Typography>
        </Box>
      </BaseToolResult>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Property Insights Results</Typography>
          <Typography variant="body2" color="text.secondary">
            Query: "{query}"
          </Typography>
        </DialogTitle>

        <DialogContent>
          {/* Summary Stats */}
          <Card sx={{ mb: 3, backgroundColor: "grey.50" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Search Summary
              </Typography>
              <Box display="flex" gap={3} flexWrap="wrap">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Documents Searched
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {summary.totalDocumentsSearched}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Insights Searched
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {summary.totalInsightsSearched}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Documents with Relevant Insights
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {summary.documentsWithRelevantInsights}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Relevant Insights Found
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {summary.totalRelevantInsights}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Document Sections */}
          {relevantDocuments.length === 0 ? (
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
              py={4}
            >
              No relevant insights found for this query.
            </Typography>
          ) : (
            relevantDocuments.map((docWithInsights) => (
              <Card key={docWithInsights.document.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={2}
                  >
                    <Box flex={1}>
                      <Box sx={{ mb: 1 }}>
                        <DocumentLink
                          documentId={docWithInsights.document.id}
                          variant="h6"
                        />
                      </Box>
                      <Box display="flex" gap={1} alignItems="center" mb={1}>
                        <Chip
                          label={docWithInsights.document.category}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {docWithInsights.relevantInsights.length} relevant
                          insight
                          {docWithInsights.relevantInsights.length !== 1
                            ? "s"
                            : ""}
                        </Typography>
                        {docWithInsights.document.publishedOn && (
                          <Typography variant="caption" color="text.secondary">
                            • Published: {docWithInsights.document.publishedOn}
                          </Typography>
                        )}
                      </Box>
                      {docWithInsights.document.description && (
                        <Typography variant="body2" color="text.secondary">
                          {docWithInsights.document.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <List dense>
                    {docWithInsights.relevantInsights.map((insight) => (
                      <ListItem key={insight.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              {insight.description}
                            </Typography>
                          }
                          secondary={
                            <Box
                              display="flex"
                              gap={1}
                              alignItems="center"
                              flexWrap="wrap"
                            >
                              {getApprovalChip(insight.approved)}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Created by:{" "}
                                {insight.createdBy === "system"
                                  ? "AI System"
                                  : insight.createdBy}
                              </Typography>
                              {insight.createdAt && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  • {insight.createdAt}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            ))
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
